import { motion } from 'framer-motion'
import { Brain, Clock, Download, Trash2 } from 'lucide-react'

const MemoryManagement = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="prose prose-invert max-w-none"
    >
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <Brain className="mr-3 text-accent-cyan" size={32} />
        记忆管理
      </h1>

      {/* 记忆系统介绍 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">什么是 AI 记忆</h2>
        <p className="text-gray-400 mb-4">
          AI 记忆是 Avelia 的长期记忆系统，用于存储对话中的重要信息、用户偏好、历史交互等内容。
          与知识库不同，记忆更关注动态的、个性化的信息。
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 not-prose">
          <div className="glass-effect rounded-lg p-5">
            <Clock className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">对话历史</h3>
            <p className="text-sm text-gray-400">
              记录你与 AI 的对话内容，支持上下文连贯的多轮对话。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <Brain className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">重要信息</h3>
            <p className="text-sm text-gray-400">
              AI 会自动提取并记住对话中的关键信息和用户偏好。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <Download className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">导出备份</h3>
            <p className="text-sm text-gray-400">
              支持导出记忆数据，方便备份和迁移。
            </p>
          </div>
        </div>
      </section>

      {/* 记忆的作用 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">记忆的作用</h2>
        
        <div className="space-y-4">
          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">🔗 上下文连贯</h3>
            <p className="text-sm text-gray-400 mb-3">
              AI 能记住之前的对话内容，实现连贯的多轮对话：
            </p>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-blue-500/10 rounded border-l-4 border-blue-500">
                <p className="text-gray-400">
                  <strong className="text-blue-300">你：</strong>"帮我分析一下这个数据文件"<br/>
                  <strong className="text-blue-300">AI：</strong>"好的，我看到这是一个销售数据表..."<br/>
                  <strong className="text-blue-300">你：</strong>"那最高销售额是多少？"<br/>
                  <strong className="text-blue-300">AI：</strong>"根据刚才分析的数据，最高销售额是..."
                </p>
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">👤 个性化体验</h3>
            <p className="text-sm text-gray-400 mb-3">
              AI 会记住你的偏好和习惯：
            </p>
            <ul className="text-sm text-gray-400 space-y-2 ml-4">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>你喜欢的回答风格（详细/简洁）</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>常用的工作流程和操作</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>你提到过的重要信息（项目名称、联系人等）</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>你的使用习惯和偏好设置</span>
              </li>
            </ul>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">📊 任务追踪</h3>
            <p className="text-sm text-gray-400 mb-3">
              记录任务执行历史和结果：
            </p>
            <ul className="text-sm text-gray-400 space-y-2 ml-4">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>之前完成过哪些任务</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>任务的执行方式和结果</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>遇到的问题和解决方案</span>
              </li>
            </ul>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">💾 主动保存记忆</h3>
            <p className="text-sm text-gray-400 mb-3">
              你可以主动让 AI 记住重要信息：
            </p>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-blue-500/10 rounded border-l-4 border-blue-500">
                <strong className="text-blue-300">示例 1：</strong>
                <p className="text-gray-400 mt-1">
                  "记住：我的项目名称是 Avelia，技术栈是 Electron + React + Python"
                </p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded border-l-4 border-blue-500">
                <strong className="text-blue-300">示例 2：</strong>
                <p className="text-gray-400 mt-1">
                  "请记住我喜欢简洁的回答风格，不要太啰嗦"
                </p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded border-l-4 border-blue-500">
                <strong className="text-blue-300">示例 3：</strong>
                <p className="text-gray-400 mt-1">
                  "记住这个重要信息：客户联系方式是..."
                </p>
              </div>
            </div>
            <div className="mt-3 p-3 bg-green-500/10 rounded border border-green-500/30 text-xs">
              <p className="text-green-300">
                💡 <strong>提示：</strong>在对话中明确说"记住"、"记下来"等关键词，AI 会将这些信息保存到记忆中。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 查看记忆 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">查看和管理记忆</h2>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              1
            </div>
            <div>
              <h3 className="font-semibold mb-2">打开记忆管理</h3>
              <p className="text-gray-400 text-sm">
                打开设置窗口，进入"记忆"页面，可以看到所有记忆条目。
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              2
            </div>
            <div>
              <h3 className="font-semibold mb-2">查看记忆详情</h3>
              <p className="text-gray-400 text-sm mb-2">
                点击任意记忆条目，可以查看完整内容：
              </p>
              <ul className="text-sm text-gray-400 space-y-1 ml-4">
                <li>• 记忆摘要</li>
                <li>• 详细信息</li>
                <li>• 创建时间</li>
              </ul>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              3
            </div>
            <div>
              <h3 className="font-semibold mb-2">搜索记忆</h3>
              <p className="text-gray-400 text-sm">
                使用搜索框快速查找特定内容的记忆。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 记忆操作 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">记忆操作</h2>
        
        <div className="glass-effect rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 font-semibold">操作</th>
                <th className="text-left p-3 font-semibold">说明</th>
              </tr>
            </thead>
            <tbody className="text-gray-400">
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">查看详情</td>
                <td className="p-3">点击记忆条目查看完整内容和详细信息</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">搜索记忆</td>
                <td className="p-3">通过关键词快速定位特定记忆</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">删除记忆</td>
                <td className="p-3">删除不需要的记忆条目</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">导出记忆</td>
                <td className="p-3">导出所有记忆为 JSON 文件，用于备份</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">清空记忆</td>
                <td className="p-3">清空所有记忆（谨慎操作，不可恢复）</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 记忆与知识库的区别 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">记忆 vs 知识库</h2>
        
        <div className="glass-effect rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 font-semibold">特性</th>
                <th className="text-left p-3 font-semibold">记忆</th>
                <th className="text-left p-3 font-semibold">知识库</th>
              </tr>
            </thead>
            <tbody className="text-gray-400">
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">内容来源</td>
                <td className="p-3">对话中自动提取</td>
                <td className="p-3">用户主动导入</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">内容类型</td>
                <td className="p-3">动态、个性化信息</td>
                <td className="p-3">静态、结构化文档</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">更新方式</td>
                <td className="p-3">自动更新</td>
                <td className="p-3">手动管理</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">使用场景</td>
                <td className="p-3">上下文对话、偏好记录</td>
                <td className="p-3">专业资料、参考文档</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">典型内容</td>
                <td className="p-3">对话历史、用户偏好</td>
                <td className="p-3">项目文档、学习笔记</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 使用建议 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">使用建议</h2>
        
        <div className="space-y-3">
          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 定期检查</h4>
            <p className="text-sm text-gray-400">
              定期查看记忆内容，确保 AI 记住的信息是准确和最新的。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 及时清理</h4>
            <p className="text-sm text-gray-400">
              删除过时或不再需要的记忆，保持记忆库的精简和高效。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 定期备份</h4>
            <p className="text-sm text-gray-400">
              使用导出功能定期备份记忆数据，防止意外丢失。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-semibold mb-2 text-yellow-300">⚠️ 隐私保护</h4>
            <p className="text-sm text-gray-400">
              记忆中可能包含敏感信息，注意保护隐私。如需分享设备，建议清空记忆。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-semibold mb-2 text-yellow-300">⚠️ 存储空间</h4>
            <p className="text-sm text-gray-400">
              记忆会占用存储空间，如果记忆过多，可能影响性能。建议保持在合理数量。
            </p>
          </div>
        </div>
      </section>

      {/* 提示 */}
      <section className="mt-8 p-6 glass-effect rounded-lg border border-accent-cyan/30">
        <h3 className="text-xl font-semibold mb-4">💡 使用技巧</h3>
        <ul className="space-y-2 text-sm text-gray-400">
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">主动保存：</strong>在对话中明确说"记住这个"、"记下来"，AI 会保存到记忆</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">引用记忆：</strong>可以说"你还记得上次我说的..."来引用之前的对话</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">更新信息：</strong>如果信息发生变化，明确告诉 AI 更新记忆</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">配合知识库：</strong>静态资料放知识库，动态信息靠记忆，两者配合使用效果更好</span>
          </li>
        </ul>
      </section>
    </motion.div>
  )
}

export default MemoryManagement
